import { BrandProfile, CustomerAvatar } from "@/hooks/useUserData";
import { Brand } from "@/contexts/BrandContext";

export interface BrandCompletenessCheck {
  isComplete: boolean;
  missingFields: string[];
}

/**
 * Maps a missing-field label (from getIncompleteFields) to the wizard step number
 * so the UI can deep-link directly to the right step.
 */
export const FIELD_TO_STEP: Record<string, number> = {
  "Brand Scan": 0,
  "Customer Avatar": 1,
  "Customer Goals": 2,
  "Customer Challenges": 3,
  "Where They Hang Out": 4,
  "Transformation Story": 5,
  "Brand Positioning": 6,
  "Value Propositions": 7,
  "Identity Pillars": 8,
  "Messaging & Taglines": 9,
  // VA-220: Brand Colors is no longer a wizard step. Colours are edited in Brand
  // Style, so anything deep-linking from this map sends the member there instead of
  // to a step that does not exist. 10 still resolves — BrandGuide redirects it.
  "Brand Colors": 10,
};

/**
 * Check if the Brand Guide has the minimum required fields to be considered "complete".
 * 
 * Delegates to `getIncompleteFields` so the gate and dashboard always show
 * the same missing-field list (11-step wizard-aligned logic).
 * 
 * Accepts an optional `brandsLoading` flag — if brands are still loading,
 * we treat the guide as complete to avoid flashing the warning during initial load.
 */
export function checkBrandGuideComplete(
  brandProfile: BrandProfile | null,
  customerAvatar: CustomerAvatar | null,
  activeBrand?: Brand | null,
  brandsLoading?: boolean
): BrandCompletenessCheck {
  // If brands are still loading, don't show incomplete warning
  if (brandsLoading) {
    return { isComplete: true, missingFields: [] };
  }

  // If activeBrand isn't available yet, treat as complete (still loading)
  if (!activeBrand) {
    return { isComplete: true, missingFields: [] };
  }

  // Use the same logic as the dashboard completion checks
  const hasAvatar = !!(customerAvatar?.avatar_name?.trim());
  
  // If useUserData hasn't loaded yet but activeBrand exists, 
  // we can't confirm avatar status — give benefit of the doubt
  const useAvatarCheck = brandProfile !== null || customerAvatar !== null;
  
  const missingFields = getIncompleteFields(
    activeBrand,
    useAvatarCheck ? hasAvatar : undefined
  );

  return {
    isComplete: missingFields.length === 0,
    missingFields
  };
}

/**
 * Returns the human-readable labels of fields that are still missing/empty for a brand.
 * When `hasAvatar` is provided, includes the 5 avatar-based wizard steps in the check.
 */
export function getIncompleteFields(brand: Brand, hasAvatar?: boolean): string[] {
  // Brand profile checks (wizard steps 6-10)
  const brandChecks = [
    { filled: !!(brand.customer_positioning || brand.expert_positioning), label: "Brand Positioning" },
    { filled: Array.isArray(brand.value_propositions) && brand.value_propositions.length > 0, label: "Value Propositions" },
    { filled: Array.isArray(brand.identity_pillars) && brand.identity_pillars.length > 0, label: "Identity Pillars" },
    { filled: !!(brand.tagline && brand.elevator_pitch), label: "Messaging & Taglines" },
    // Any core brand color counts — not primary specifically. Must match the
    // wizard step-10 check in BrandGuideWizard.tsx.
    { filled: !!(brand.primary_color || brand.secondary_color || brand.tertiary_color || brand.accent_color), label: "Brand Colors" },
  ];

  // Avatar checks (wizard steps 1-5) — if hasAvatar is provided, count them
  const avatarChecks = hasAvatar !== undefined ? [
    { filled: hasAvatar, label: "Customer Avatar" },
    { filled: hasAvatar, label: "Customer Goals" },
    { filled: hasAvatar, label: "Customer Challenges" },
    { filled: hasAvatar, label: "Where They Hang Out" },
    { filled: hasAvatar, label: "Transformation Story" },
  ] : [];

  // Scan step (step 0) — has any meaningful brand data at all
  const scanCheck = hasAvatar !== undefined ? [
    { filled: !!(brand.customer_positioning || brand.tagline || hasAvatar), label: "Brand Scan" },
  ] : [];

  return [...scanCheck, ...avatarChecks, ...brandChecks].filter(c => !c.filled).map(c => c.label);
}

/**
 * Calculate brand guide completion percentage based on filled fields.
 * When `hasAvatar` is provided, uses the full 11-step wizard logic
 * (5 avatar steps + 1 scan step + 5 brand profile steps).
 * Without `hasAvatar`, falls back to brand-profile-only checks (legacy).
 */
export function calculateBrandCompletion(brand: Brand, hasAvatar?: boolean): number {
  if (hasAvatar !== undefined) {
    // Full 11-step wizard-aligned checks
    const checks = [
      // Step 0 (Scan): has any analyzed data
      !!(brand.customer_positioning || brand.tagline || hasAvatar),
      // Steps 1-5 (Avatar): all pass/fail together based on avatar existence
      hasAvatar, // Customer
      hasAvatar, // Goals
      hasAvatar, // Pains
      hasAvatar, // Where
      hasAvatar, // Transformation
      // Step 6 (Positioning)
      !!(brand.customer_positioning || brand.expert_positioning),
      // Step 7 (Value Props)
      Array.isArray(brand.value_propositions) && brand.value_propositions.length > 0,
      // Step 8 (Identity Pillars)
      Array.isArray(brand.identity_pillars) && brand.identity_pillars.length > 0,
      // Step 9 (Taglines): tagline + elevator pitch
      !!(brand.tagline && brand.elevator_pitch),
      // Step 10 (Colors): any core brand color
      !!(brand.primary_color || brand.secondary_color || brand.tertiary_color || brand.accent_color),
    ];
    const filled = checks.filter(Boolean).length;
    return Math.round((filled / checks.length) * 100);
  }

  // Legacy fallback (no avatar info available)
  const checks = [
    !!(brand.customer_positioning || brand.expert_positioning),
    Array.isArray(brand.value_propositions) && brand.value_propositions.length > 0,
    Array.isArray(brand.identity_pillars) && brand.identity_pillars.length > 0,
    !!(brand.tagline && brand.elevator_pitch),
    !!(brand.primary_color || brand.secondary_color || brand.tertiary_color || brand.accent_color),
  ];

  const filled = checks.filter(Boolean).length;
  return Math.round((filled / checks.length) * 100);
}
